import {ConsentPurpose} from '@/types'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {Chip, Stack} from '@mui/material'
import * as React from 'react'
import {Fragment, useEffect} from 'react'
import {
	Button,
	Datagrid,
	DateField,
	FunctionField,
	List,
	Pagination,
	SearchInput,
	TextField,
	useDataProvider,
	useNotify,
	useRefresh
} from 'react-admin'

export default function ConsentPurposeList() {
	return (
		<List
			sort={{field: 'code', order: 'ASC'}}
			perPage={25}
			filters={[<SearchInput name="q" source="q" alwaysOn/>]}
			exporter={false}
		>
			<TableList/>
		</List>
	)
}

const TableList = () => {
	const dataProvider = useDataProvider()
	const notify = useNotify()
	const refresh = useRefresh()

	useEffect((): any => {
		document.title = 'Consentements | Usages RGPD'
	}, [])

	const handleActivate = async (id: number) => {
		try {
			await dataProvider.update('consent/purposes', {
				id,
				data: {},
				previousData: {id},
				meta: {action: 'activate'}
			})
			notify('Usage activé avec succès', {type: 'success'})
			refresh()
		} catch (error) {
			notify('Erreur lors de l\'activation', {type: 'error'})
		}
	}

	const handleDeactivate = async (id: number) => {
		try {
			await dataProvider.update('consent/purposes', {
				id,
				data: {},
				previousData: {id},
				meta: {action: 'deactivate'}
			})
			notify('Usage désactivé avec succès', {type: 'success'})
			refresh()
		} catch (error: any) {
			const message = error?.body?.error || 'Erreur lors de la désactivation'
			notify(message, {type: 'error'})
		}
	}

	return (
		<Fragment>
			<Pagination rowsPerPageOptions={[5, 10, 25]}/>
			<Datagrid optimized rowClick="show">
				<TextField source="code" label="Code"/>
				<TextField source="title" label="Titre"/>
				<TextField source="version" label="Version"/>

				<FunctionField
					source="versionType"
					label="Type de version"
					render={(record: ConsentPurpose) => (
						<Chip
							label={record.versionType === 'MAJOR' ? 'Majeure' : 'Mineure'}
							color={record.versionType === 'MAJOR' ? 'primary' : 'default'}
							size="small"
						/>
					)}
				/>

				<FunctionField
					source="isActive"
					label="Statut"
					render={(record: ConsentPurpose) => (
						<Chip
							label={record.isActive ? 'Actif' : 'Inactif'}
							color={record.isActive ? 'success' : 'default'}
							icon={record.isActive ? <CheckCircleIcon/> : <CancelIcon/>}
							size="small"
						/>
					)}
				/>

				<FunctionField
					source="userConsentCount"
					label="Nb utilisateurs"
					render={(record: ConsentPurpose) => (
						<span>{record.userConsentCount || 0}</span>
					)}
				/>

				<DateField source="createdAt" label="Créé le"/>

				<FunctionField
					label="Actions"
					render={(record: ConsentPurpose) => (
						<Stack direction="row" spacing={1}>
							{!record.isActive ? (
								<Button
									label="Activer"
									onClick={() => handleActivate(record.id)}
									size="small"
								/>
							) : (
								<Button
									label="Désactiver"
									onClick={() => handleDeactivate(record.id)}
									size="small"
								/>
							)}
						</Stack>
					)}
				/>
			</Datagrid>
		</Fragment>
	)
}
